/** @format */

import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, CircleOff } from "lucide-react";
import {
  FLAG_CAD_PIPELINE,
  FLAG_COMPARE_VERSIONS,
  FLAG_DRAWINGS_PIPELINE,
  FLAG_IMAGE_PIPELINE,
  FLAG_RMA_ANALYSIS,
  FLAG_TEST_PROCEDURES,
  FLAG_TRACK_CHANGES,
  FLAG_VIDEO_PIPELINE,
  FLAG_VIDEO_REALTIME_WORKFLOW,
} from "../../lib/featureFlags";
import { orgFeaturesService } from "../../api/orgFeaturesService";
import { Button } from "../ui/button";

const ALL_FLAGS = [
  FLAG_CAD_PIPELINE,
  FLAG_DRAWINGS_PIPELINE,
  FLAG_VIDEO_PIPELINE,
  FLAG_IMAGE_PIPELINE,
  FLAG_TEST_PROCEDURES,
  FLAG_RMA_ANALYSIS,
  FLAG_TRACK_CHANGES,
  FLAG_COMPARE_VERSIONS,
];

const FEATURE_FLAG_META: Record<
  string,
  { label: string; description: string }
> = {
  [FLAG_TEST_PROCEDURES]: {
    label: "Test Procedures",
    description: "Enable guided test-procedure generation and workflows.",
  },
  [FLAG_DRAWINGS_PIPELINE]: {
    label: "Assembly Drawings Pipeline",
    description: "Enable SOP generation from drawing files.",
  },
  [FLAG_VIDEO_PIPELINE]: {
    label: "Video Workflow Pipeline",
    description: "Enable SOP generation from video clips.",
  },
  [FLAG_IMAGE_PIPELINE]: {
    label: "Real-world Image Pipeline",
    description: "Enable SOP generation from image uploads.",
  },
  [FLAG_RMA_ANALYSIS]: {
    label: "RMA Analysis",
    description: "Enable RMA dashboards, analysis, and dataset screens.",
  },
  [FLAG_TRACK_CHANGES]: {
    label: "Track Changes",
    description: "Enable SOP review timelines and change tracking.",
  },
  [FLAG_COMPARE_VERSIONS]: {
    label: "Compare Versions",
    description: "Enable side-by-side version comparisons for SOPs.",
  },
  [FLAG_CAD_PIPELINE]: {
    label: "CAD Pipeline",
    description: "Enable CAD model ingestion and CAD SOP pipeline.",
  },
};

interface Props {
  orgId?: string;
  initialFeatures?: Record<string, boolean>;
  onSaved?: (features: Record<string, boolean>) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const normalizeFeaturesForPayload = (source: Record<string, boolean>) => {
  const base: Record<string, boolean> = {};
  for (const key of ALL_FLAGS) base[key] = source[key] === true;
  // Keep legacy key in sync for backwards compatibility.
  base[FLAG_VIDEO_REALTIME_WORKFLOW] =
    base[FLAG_VIDEO_PIPELINE] === true || base[FLAG_IMAGE_PIPELINE] === true;
  return base;
};

export default function OrgFeatureFlagsEditor({
  orgId,
  initialFeatures,
  onSaved,
  onDirtyChange,
}: Props) {
  const [features, setFeatures] = useState<Record<string, boolean>>(
    initialFeatures ?? {},
  );
  const [saving, setSaving] = useState(false);

  const hydrateFeaturesForEditor = (source?: Record<string, boolean>) => {
    const next = { ...(source ?? {}) };
    if (next[FLAG_VIDEO_REALTIME_WORKFLOW] === true) {
      if (next[FLAG_VIDEO_PIPELINE] === undefined) {
        next[FLAG_VIDEO_PIPELINE] = true;
      }
      if (next[FLAG_IMAGE_PIPELINE] === undefined) {
        next[FLAG_IMAGE_PIPELINE] = true;
      }
    }
    return next;
  };

  useEffect(() => {
    setFeatures(hydrateFeaturesForEditor(initialFeatures));
  }, [orgId, initialFeatures]);

  const normalized = useMemo(() => {
    return normalizeFeaturesForPayload(features);
  }, [features]);

  const normalizedInitial = useMemo(() => {
    const hydratedInitial = hydrateFeaturesForEditor(initialFeatures);
    return normalizeFeaturesForPayload(hydratedInitial);
  }, [initialFeatures]);

  useEffect(() => {
    const isDirty = JSON.stringify(normalized) !== JSON.stringify(normalizedInitial);
    onDirtyChange?.(isDirty);
  }, [normalized, normalizedInitial, onDirtyChange]);

  const toggle = (key: string) => {
    setFeatures((prev) => {
      const next = { ...prev, [key]: prev[key] !== true };
      // In create-invite mode (no orgId), apply immediately to parent form.
      if (!orgId) {
        onSaved?.(normalizeFeaturesForPayload(next));
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (!orgId) {
        onSaved?.(normalized);
        return;
      }
      const next = await orgFeaturesService.put(orgId, normalized);
      setFeatures(next);
      onSaved?.(next);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-slate-700/40 bg-slate-900/40 p-4">
      <div className="space-y-2">
        {ALL_FLAGS.map((flag) => (
          <button
            key={flag}
            type="button"
            onClick={() => toggle(flag)}
            className={`group flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
              normalized[flag] === true
                ? "border-blue-500/60 bg-blue-500/10 shadow-[0_0_0_1px_rgba(59,130,246,0.15)]"
                : "border-slate-700/40 bg-transparent hover:border-slate-600 hover:bg-slate-800/40"
            }`}
            aria-pressed={normalized[flag] === true}
          >
            <div>
              <p className="text-xs font-medium text-slate-100">
                {FEATURE_FLAG_META[flag]?.label ?? flag}
              </p>
              <p className="mt-0.5 text-[11px] text-slate-400">
                {FEATURE_FLAG_META[flag]?.description ?? flag}
              </p>
            </div>
            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                normalized[flag] === true
                  ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-200"
                  : "border-slate-600/70 bg-slate-800/70 text-slate-300"
              }`}
            >
              {normalized[flag] ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <CircleOff className="h-3.5 w-3.5" />
              )}
              {normalized[flag] ? "Enabled" : "Disabled"}
            </span>
          </button>
        ))}
      </div>
      {orgId && (
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-md border border-blue-500/30 bg-blue-500/20 px-3 py-1.5 text-sm text-blue-200 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save changes"}
        </Button>
      )}
      {/* <button
        type="button"
        onClick={save}
        disabled={saving}
        className="rounded-md border border-blue-500/30 bg-blue-500/20 px-3 py-1.5 text-sm text-blue-200 disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save changes"}
      </button> */}
    </div>
  );
}
